import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Send, Check, CalendarClock, Upload, RotateCcw, Play, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Field, SaveBtn, inputCls } from "./kit";

const STATUS_COLORS = {
  draft: "border-paper/20 text-paper/50",
  in_review: "border-amber-400/40 text-amber-300",
  approved: "border-sky-400/40 text-sky-300",
  scheduled: "border-violet-400/40 text-violet-300",
  published: "border-gold/40 text-gold",
  unpublished: "border-paper/20 text-paper/40",
};

function Pill({ status }) {
  return (
    <span className={`font-ui text-[10px] uppercase tracking-[0.2em] px-3 py-1 rounded-full border ${STATUS_COLORS[status] || "border-paper/20 text-paper/40"}`}>
      {(status || "").replace("_", " ")}
    </span>
  );
}

function fmt(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en-US", { timeZone: "America/New_York", dateStyle: "medium", timeStyle: "short" }); }
  catch { return String(iso).slice(0, 16); }
}

function QueuePanel({ queue, settings, onSaveSettings, onRunNow, running }) {
  const [s, setS] = useState(settings);
  useEffect(() => setS(settings), [settings]);
  if (!queue || !s) return null;
  const stat = [
    { label: "Drafts", value: queue.drafts },
    { label: "In review", value: queue.in_review },
    { label: "Approved (auto queue)", value: queue.approved_auto },
    { label: "Scheduled", value: queue.scheduled },
    { label: "Published today", value: queue.published_today },
    { label: "Published total", value: queue.total_published },
  ];
  return (
    <div className="border border-paper/10 bg-coal p-6 mb-8" data-testid="daily-queue-panel">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-5">
        <h3 className="font-display text-xl text-paper">Publishing engine</h3>
        <button onClick={onRunNow} disabled={running} data-testid="daily-run-now-btn"
          className="inline-flex items-center gap-2 font-ui text-[11px] uppercase tracking-[0.2em] border border-paper/25 text-paper/80 px-5 py-2.5 rounded-full hover:border-paper/60 transition-colors duration-300 disabled:opacity-50">
          <Play size={13} /> {running ? "Running…" : "Run publisher now"}
        </button>
      </div>
      {queue.low && (
        <p className="font-ui text-xs text-amber-300 border border-amber-400/30 bg-amber-400/5 rounded-md px-4 py-3 mb-5" data-testid="daily-queue-low-alert">
          ⚠ Approved queue is low — only {queue.queue_stock} ready (threshold {queue.threshold}). Approve more Chronicles to keep daily publishing going.
        </p>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
        {stat.map((c) => (
          <div key={c.label} className="border border-paper/10 p-4" data-testid={`daily-stat-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <p className="font-display text-2xl text-paper">{c.value ?? "—"}</p>
            <p className="font-ui text-[10px] uppercase tracking-[0.18em] text-paper/45 mt-1">{c.label}</p>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border-t border-paper/10 pt-5">
        <div>
          <label className="block font-ui text-[11px] uppercase tracking-[0.2em] text-paper/50 mb-2">Daily publish time (ET, 24h)</label>
          <input value={s.publish_time || ""} onChange={(e) => setS({ ...s, publish_time: e.target.value })}
            placeholder="06:00" className={inputCls} data-testid="daily-set-time" />
        </div>
        <div>
          <label className="block font-ui text-[11px] uppercase tracking-[0.2em] text-paper/50 mb-2">Low-queue alert threshold</label>
          <input type="number" value={s.queue_low_threshold ?? 3} onChange={(e) => setS({ ...s, queue_low_threshold: Number(e.target.value) })}
            className={inputCls} data-testid="daily-set-threshold" />
        </div>
        <label className="inline-flex items-center gap-3 cursor-pointer">
          <input type="checkbox" checked={!!s.auto_enabled} onChange={(e) => setS({ ...s, auto_enabled: e.target.checked })}
            className="accent-[#B71F32] w-4 h-4" data-testid="daily-set-auto" />
          <span className="font-ui text-sm text-paper/70">Auto-publish from the approved queue each day</span>
        </label>
        <div className="sm:text-right">
          <SaveBtn onClick={() => onSaveSettings(s)} label="Save settings" tid="daily-save-settings" />
        </div>
      </div>
      <p className="font-ui text-xs text-paper/35 mt-4">
        Timezone: America/New_York (DST handled automatically). A specifically scheduled Chronicle always takes priority over the auto queue, and only one Chronicle is released per day.
      </p>
    </div>
  );
}

export function DailyTab() {
  const [items, setItems] = useState(null);
  const [queue, setQueue] = useState(null);
  const [settings, setSettings] = useState(null);
  const [log, setLog] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [sched, setSched] = useState(null); // {id, publish_at}

  const loadAll = () => {
    api.get("/admin/chronicles").then((r) => setItems(r.data)).catch(() => setItems([]));
    api.get("/admin/daily/queue").then((r) => setQueue(r.data)).catch(() => {});
    api.get("/admin/daily/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/admin/daily/log").then((r) => setLog(r.data)).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []);

  const save = async () => {
    if (!editing.title || !editing.body) { toast.error("Title and manuscript body are required."); return; }
    setSaving(true);
    try {
      let res;
      if (editing.chronicle_id) res = await api.put(`/admin/chronicles/${editing.chronicle_id}`, editing);
      else res = await api.post("/admin/chronicles", editing);
      toast.success(res.data?.reapproval_required ? "Saved — substantive edit, re-approval required" : "Saved");
      setEditing(null); loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const act = async (id, path, okMsg) => {
    try { await api.post(`/admin/chronicles/${id}/${path}`); toast.success(okMsg); loadAll(); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete Chronicle No. ${c.number} permanently? Its manuscript and version history will be lost.`)) return;
    await api.delete(`/admin/chronicles/${c.chronicle_id}`); toast.success("Deleted"); loadAll();
  };

  const saveSettings = async (s) => {
    try { await api.put("/admin/daily/settings", s); toast.success("Settings saved"); loadAll(); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await api.post("/admin/daily/run-now");
      const a = r.data?.action;
      toast.success(a?.startsWith("published") ? `Published: ${r.data.published?.title}` : a === "already_published_today" ? "Already published today" : "Ran — nothing due");
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Run failed"); }
    finally { setRunning(false); }
  };

  const doSchedule = async () => {
    try {
      await api.post(`/admin/chronicles/${sched.id}/schedule`, { publish_at: sched.publish_at });
      toast.success("Scheduled"); setSched(null); loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Schedule failed"); }
  };

  const wc = (editing?.body || "").trim().split(/\s+/).filter(Boolean).length;

  return (
    <div data-testid="admin-tab-daily">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl text-paper">The Daily Chronicle</h2>
          <p className="font-ui text-xs text-paper/40 mt-1">Original daily prophetic writings. Draft → In review → Approved → Scheduled/Auto → Published.</p>
        </div>
        <button onClick={() => setEditing({ status: "draft" })} data-testid="daily-new-btn"
          className="font-ui text-[11px] uppercase tracking-[0.2em] font-semibold bg-crimson text-paper px-5 py-2.5 rounded-full hover:bg-crimsonhi transition-colors duration-300">
          New Chronicle
        </button>
      </div>

      <QueuePanel queue={queue} settings={settings} onSaveSettings={saveSettings} onRunNow={runNow} running={running} />

      {items === null ? (
        <p className="font-ui text-sm text-paper/40">Loading…</p>
      ) : items.length === 0 ? (
        <p className="font-ui text-sm text-paper/40 border border-paper/10 p-8" data-testid="daily-list-empty">No Chronicles yet. Write the first one.</p>
      ) : (
        <div className="divide-y divide-paper/10 border-y border-paper/10" data-testid="daily-list">
          {items.map((c) => (
            <div key={c.chronicle_id} className="py-4 flex items-center justify-between gap-4 flex-wrap" data-testid={`daily-row-${c.chronicle_id}`}>
              <div className="min-w-0">
                <p className="font-ui text-sm text-paper truncate">
                  <span className="text-crimson">No. {c.number}</span> · {c.title}
                </p>
                <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                  <Pill status={c.status} />
                  <span className="font-ui text-xs text-paper/40">{c.word_count} words</span>
                  {c.status === "scheduled" && c.publish_at && (
                    <span className="font-ui text-xs text-violet-300/80 inline-flex items-center gap-1"><Clock size={11} /> {fmt(c.publish_at)}</span>
                  )}
                  {c.status === "published" && c.published_at && (
                    <span className="font-ui text-xs text-gold/70">{fmt(c.published_at)}</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                {c.status === "draft" && (
                  <button onClick={() => act(c.chronicle_id, "submit", "Submitted for review")} title="Submit for review" data-testid={`daily-submit-${c.chronicle_id}`} className="p-2 text-paper/50 hover:text-amber-300 transition-colors"><Send size={15} /></button>
                )}
                {(c.status === "draft" || c.status === "in_review") && (
                  <button onClick={() => act(c.chronicle_id, "approve", "Approved")} title="Approve" data-testid={`daily-approve-${c.chronicle_id}`} className="p-2 text-paper/50 hover:text-sky-300 transition-colors"><Check size={15} /></button>
                )}
                {(c.status === "approved" || c.status === "scheduled") && (
                  <button onClick={() => setSched({ id: c.chronicle_id, publish_at: "" })} title="Schedule" data-testid={`daily-schedule-${c.chronicle_id}`} className="p-2 text-paper/50 hover:text-violet-300 transition-colors"><CalendarClock size={15} /></button>
                )}
                {(c.status === "approved" || c.status === "scheduled" || c.status === "unpublished") && (
                  <button onClick={() => act(c.chronicle_id, "publish", "Published now")} title="Publish now" data-testid={`daily-publish-${c.chronicle_id}`} className="p-2 text-paper/50 hover:text-gold transition-colors"><Play size={15} /></button>
                )}
                {c.status === "published" && (
                  <button onClick={() => act(c.chronicle_id, "unpublish", "Unpublished")} title="Unpublish" data-testid={`daily-unpublish-${c.chronicle_id}`} className="p-2 text-paper/50 hover:text-crimsonhi transition-colors"><RotateCcw size={15} /></button>
                )}
                <button onClick={() => setEditing(c)} title="Edit" data-testid={`daily-edit-${c.chronicle_id}`} className="p-2 text-paper/50 hover:text-paper transition-colors"><Pencil size={15} /></button>
                <button onClick={() => remove(c)} title="Delete" data-testid={`daily-delete-${c.chronicle_id}`} className="p-2 text-paper/50 hover:text-crimsonhi transition-colors"><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Publication log */}
      {log.length > 0 && (
        <div className="mt-10">
          <h3 className="font-display text-xl text-paper mb-4">Publication history</h3>
          <div className="border border-paper/10 divide-y divide-paper/10" data-testid="daily-log">
            {log.slice(0, 20).map((l, i) => (
              <div key={i} className="px-4 py-3 flex items-center gap-4 flex-wrap">
                <span className={`font-ui text-[10px] uppercase tracking-[0.2em] ${l.status === "ok" ? "text-gold" : l.status === "queue_low" ? "text-amber-300" : "text-crimsonhi"}`}>{l.status}</span>
                <span className="font-ui text-xs text-paper/60">{fmt(l.ts)}</span>
                <span className="font-ui text-xs text-paper/50">{l.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Editor */}
      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent className="bg-coal border-paper/15 max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="daily-editor">
          <DialogTitle className="font-display text-xl text-paper">{editing?.chronicle_id ? `Edit — Chronicle No. ${editing.number}` : "New Chronicle"}</DialogTitle>
          {editing && (
            <div className="mt-4">
              <Field label="Title"><input value={editing.title || ""} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className={inputCls} data-testid="daily-edit-title" /></Field>
              <Field label="Scripture references" hint="Comma or line separated, e.g. Matthew 23:27, Revelation 3:16">
                <input value={Array.isArray(editing.scripture_refs) ? editing.scripture_refs.join(", ") : (editing.scripture_refs || "")}
                  onChange={(e) => setEditing({ ...editing, scripture_refs: e.target.value })} className={inputCls} data-testid="daily-edit-scriptures" />
              </Field>
              <Field label="Theme" hint="One short theme, e.g. Repentance, Accountability">
                <input value={editing.theme || ""} onChange={(e) => setEditing({ ...editing, theme: e.target.value })} className={inputCls} data-testid="daily-edit-theme" />
              </Field>
              <Field label="Tags (optional)" hint="Comma separated — used for search and future book collections">
                <input value={Array.isArray(editing.tags) ? editing.tags.join(", ") : (editing.tags || "")}
                  onChange={(e) => setEditing({ ...editing, tags: e.target.value })} className={inputCls} data-testid="daily-edit-tags" />
              </Field>
              <Field label={`Manuscript (${wc} words — aim for 500–800)`} hint="Separate paragraphs with a blank line. Opening · exposition · confrontation · Kingdom application · closing declaration.">
                <textarea rows={16} value={editing.body || ""} onChange={(e) => setEditing({ ...editing, body: e.target.value })} className={`${inputCls} resize-y font-reading`} data-testid="daily-edit-body" />
              </Field>
              {editing.chronicle_id && ["approved", "scheduled", "published"].includes(editing.status) && (
                <p className="font-ui text-xs text-amber-300/90 mb-4">Note: a substantive edit (title, body, or scripture) returns this Chronicle to review and requires your renewed approval.</p>
              )}
              <div className="flex justify-end gap-3 mt-2">
                <button onClick={() => setEditing(null)} data-testid="daily-editor-cancel" className="font-ui text-xs uppercase tracking-[0.2em] text-paper/60 px-5 py-3">Cancel</button>
                <SaveBtn onClick={save} saving={saving} label="Save manuscript" tid="daily-editor-save" />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Schedule dialog */}
      <Dialog open={!!sched} onOpenChange={() => setSched(null)}>
        <DialogContent className="bg-coal border-paper/15 max-w-md" data-testid="daily-schedule-dialog">
          <DialogTitle className="font-display text-xl text-paper">Schedule Chronicle</DialogTitle>
          <div className="mt-4">
            <Field label="Publish date & time (Eastern / New York)" hint="This takes priority over the auto queue on that day.">
              <input type="datetime-local" value={sched?.publish_at || ""} onChange={(e) => setSched({ ...sched, publish_at: e.target.value })} className={inputCls} data-testid="daily-schedule-input" />
            </Field>
            <div className="flex justify-end gap-3 mt-2">
              <button onClick={() => setSched(null)} className="font-ui text-xs uppercase tracking-[0.2em] text-paper/60 px-5 py-3">Cancel</button>
              <SaveBtn onClick={doSchedule} label="Schedule" tid="daily-schedule-confirm" />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
